import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pre-baked rotating pool used when there is no real recent signup.
// Cycle every 2 days; same entry shown to all visitors within that 48-hour window.
const FAKE_NAMES = [
  { first: 'Sophie', city: 'London' },
  { first: 'James', city: 'Birmingham' },
  { first: 'Aisha', city: 'Manchester' },
  { first: 'Daniel', city: 'Leeds' },
  { first: 'Chloe', city: 'Bristol' },
  { first: 'Liam', city: 'Liverpool' },
  { first: 'Priya', city: 'Leicester' },
  { first: 'Jack', city: 'Nottingham' },
  { first: 'Emily', city: 'Sheffield' },
  { first: 'Hannah', city: 'Coventry' },
  { first: 'Ryan', city: 'Wolverhampton' },
  { first: 'Sarah', city: 'Oxford' },
  { first: 'Aaron', city: 'Reading' },
  { first: 'Fatima', city: 'Luton' },
  { first: 'Olivia', city: 'Cambridge' },
  { first: 'Kyle', city: 'Newcastle' },
  { first: 'Zoe', city: 'Brighton' },
  { first: 'Tom', city: 'Glasgow' },
  { first: 'Ruby', city: 'Cardiff' },
  { first: 'Megan', city: 'Edinburgh' },
];

// Mulberry32 PRNG — seeded so the same cycle gives the same pick across all visitors
function seededRandom(seed) {
  let t = seed + 0x6d2b79f5;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

function pickForCycle(cycleIndex) {
  const r = seededRandom(cycleIndex);
  return FAKE_NAMES[Math.floor(r * FAKE_NAMES.length)];
}

export default function PilotStatusWidget() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/pilot/status`);
        if (!cancelled) setStatus(res.data);
      } catch (err) {
        // fail silently — widget just hides if API fails
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !status) return null;

  // Compute which fake entry to show this cycle (cycle = 2 days)
  const now = Date.now();
  const cycleIndex = Math.floor(now / (1000 * 60 * 60 * 24 * 2));
  const fakePick = pickForCycle(cycleIndex);

  // Prefer a real recent signup if we have one (last 7 days)
  let displayName = null;
  let displaySource = 'fake';
  if (status.recent_real_signup?.first_name) {
    displayName = status.recent_real_signup.first_name;
    displaySource = 'real';
  } else {
    displayName = `${fakePick.first} from ${fakePick.city}`;
  }

  const total = status.total_places;
  // Simulate at least 1 person joined so the counter reflects the rolling
  // activity line below (which always claims "X just joined the pilot").
  const taken = Math.max(status.places_taken, 1);
  const placesLeft = Math.max(0, total - taken);
  const isFull = status.is_full;
  const filledPct = Math.min(100, Math.round((taken / total) * 100));

  return (
    <div className="mt-3 mb-6" data-testid="pilot-status-widget">
      {/* Places left */}
      <div className="flex items-center gap-2.5 mb-2.5">
        <Users className="w-4 h-4 text-primary flex-shrink-0" />
        <p className="text-sm sm:text-base font-semibold text-dark">
          {isFull ? (
            <span>Pilot is full — sign up for the next round</span>
          ) : (
            <>
              <span className="text-red-600">Only {placesLeft} {placesLeft === 1 ? 'place' : 'places'} left</span>
              <span className="text-text-secondary font-normal"> in our pilot programme</span>
            </>
          )}
        </p>
      </div>

      {/* Progress bar */}
      <div className="w-full h-1.5 bg-border rounded-full overflow-hidden mb-3">
        <div
          className="h-full bg-primary transition-all duration-500"
          style={{ width: `${filledPct}%` }}
          aria-hidden="true"
        />
      </div>

      {/* Rolling activity line */}
      <div
        key={cycleIndex /* re-mount when cycle flips */}
        className="flex items-center gap-2 text-xs sm:text-sm text-text-secondary animate-fade-in"
        data-testid="pilot-rolling-activity"
      >
        <span className="relative flex h-2 w-2 flex-shrink-0">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-60"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
        </span>
        <Sparkles className="w-3.5 h-3.5 text-primary flex-shrink-0" />
        <span className="truncate">
          <span className="font-medium text-dark">{displayName}</span>
          {' '}just joined the pilot
        </span>
      </div>
    </div>
  );
}
